import React, { createContext, useContext, useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import { clearAiStudioSessions } from '../utils/haircutsStudioSession';

const SuperadminAuthContext = createContext();

export const useSuperadminAuth = () => {
  const context = useContext(SuperadminAuthContext);
  if (!context) {
    throw new Error('useSuperadminAuth must be used within a SuperadminAuthProvider');
  }
  return context;
};

export const SuperadminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState(null);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await axiosClient.get('/auth/me');

      if (response.data.ok) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Check for impersonation cookie
        const impersonationCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('impersonation_token='));
        
        if (impersonationCookie) {
          setIsImpersonating(true);
          // Try to get impersonation info from URL params or localStorage
          const urlParams = new URLSearchParams(window.location.search);
          const shopId = urlParams.get('shopId');
          
          // Store basic impersonation info (we'll get full details from the backend if needed)
          setImpersonationInfo({ 
            active: true,
            shopId: shopId,
            // We could make an API call here to get full shop details if needed
          });
        } else {
          setIsImpersonating(false);
          setImpersonationInfo(null);
        }
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      localStorage.removeItem('user');
      setError(err.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearAiStudioSessions();
    setUser(null);
    setError(null);
    setIsImpersonating(false);
    setImpersonationInfo(null);
  };

  const hasSuperadminAccess = () => {
    if (!user) return false;
    return user.role === 'superadmin';
  };

  // Superadmin-specific API calls
  const getShops = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/shops?${queryString}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching shops:', err);
      throw err;
    }
  };

  const createShop = async (shopData) => {
    try {
      const response = await axiosClient.post('/superadmin/shops', shopData);
      return response.data;
    } catch (err) {
      console.error('Error creating shop:', err);
      throw err;
    }
  };

  const updateShop = async (shopId, updates) => {
    try {
      const response = await axiosClient.patch(`/superadmin/shops/${shopId}`, updates);
      return response.data;
    } catch (err) {
      console.error('Error updating shop:', err);
      throw err;
    }
  };

  const impersonate = async (shopId) => {
    try {
      const response = await axiosClient.post('/superadmin/impersonate', { shop_id: shopId });
      if (response.data.ok) {
        setIsImpersonating(true);
        setImpersonationInfo({
          active: true,
          shop: response.data.data.shop,
          impersonated_user: response.data.data.impersonated_user
        });
        return response.data;
      }
      throw new Error('Impersonation failed');
    } catch (err) {
      console.error('Error starting impersonation:', err);
      throw err;
    }
  };

  const exitImpersonation = async () => {
    try {
      const response = await axiosClient.post('/superadmin/impersonate/exit');
      if (response.data.ok) {
        setIsImpersonating(false);
        setImpersonationInfo(null);
        return response.data;
      }
      throw new Error('Exit impersonation failed');
    } catch (err) {
      console.error('Error exiting impersonation:', err);
      throw err;
    }
  };

  const getUsers = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/users?${queryString}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching users:', err);
      throw err;
    }
  };

  const getDashboardOverview = async () => {
    try {
      const response = await axiosClient.get('/superadmin/dashboard/overview');
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin dashboard overview:', err);
      throw err;
    }
  };

  const getDashboardTrends = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/dashboard/trends${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin dashboard trends:', err);
      throw err;
    }
  };

  const getAdmins = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/admins${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin admins:', err);
      throw err;
    }
  };

  const getAdminById = async (adminId) => {
    try {
      const response = await axiosClient.get(`/superadmin/admins/${adminId}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin admin details:', err);
      throw err;
    }
  };

  const getShopBarbers = async (shopId, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/shops/${shopId}/barbers${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin shop barbers:', err);
      throw err;
    }
  };

  const getShopClients = async (shopId, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/shops/${shopId}/clients${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin shop clients:', err);
      throw err;
    }
  };

  const getShopBookings = async (shopId, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/shops/${shopId}/bookings${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin shop bookings:', err);
      throw err;
    }
  };

  const getShopAiImages = async (shopId, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/shops/${shopId}/ai-images${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin shop AI images:', err);
      throw err;
    }
  };

  const getShopUsageSummary = async (shopId, params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/shops/${shopId}/usage-summary${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching superadmin shop usage summary:', err);
      throw err;
    }
  };

  const provisionAdmin = async (payload) => {
    try {
      const response = await axiosClient.post('/superadmin/admins/provision', payload);
      return response.data;
    } catch (err) {
      console.error('Error provisioning superadmin-managed admin:', err);
      throw err;
    }
  };

  const getAuditLogs = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await axiosClient.get(`/superadmin/audit-log?${queryString}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      throw err;
    }
  };

  const getSystemInfo = async () => {
    try {
      const response = await axiosClient.get('/superadmin/system');
      return response.data;
    } catch (err) {
      console.error('Error fetching system info:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const value = {
    user,
    loading,
    error,
    isImpersonating,
    impersonationInfo,
    fetchUserData,
    logout,
    hasSuperadminAccess,
    // API methods
    getShops,
    createShop,
    updateShop,
    impersonate,
    exitImpersonation,
    getUsers,
    getDashboardOverview,
    getDashboardTrends,
    getAdmins,
    getAdminById,
    getShopBarbers,
    getShopClients,
    getShopBookings,
    getShopAiImages,
    getShopUsageSummary,
    provisionAdmin,
    getAuditLogs,
    getSystemInfo,
  };

  return (
    <SuperadminAuthContext.Provider value={value}>
      {children}
    </SuperadminAuthContext.Provider>
  );
};
